import React, { useEffect, useState, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  BarElement, LineElement, PointElement,
  Title, Tooltip, Legend,
} from 'chart.js';
import DataLabelsPlugin from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, annotationPlugin);
ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend, DataLabelsPlugin);

const API = window.location.hostname === 'localhost'
  ? 'http://localhost:5000/api'
  : 'https://demo.myssa.net/api';

// ─── Formatters ───────────────────────────────────────────────────────────────
const fmt = (n) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
const fmtQty = (n) => (n == null ? '—' : Number(n).toLocaleString('fr-FR'));

const parseLocalDate = (str) => {
  if (!str) return null;
  if (str instanceof Date) return isNaN(str) ? null : str;
  if (typeof str !== 'string') { const d = new Date(str); return isNaN(d) ? null : d; }
  const parts = str.split('/');
  if (parts.length === 3) return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
  return new Date(str);
};

const getMoisLabel = (offset) => {
  const now = new Date();
  const base = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  return base.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
};

const getMonthKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const getWeekKey = (d) => {
  const tmp = new Date(d); tmp.setHours(0, 0, 0, 0);
  const day = tmp.getDay() || 7; tmp.setDate(tmp.getDate() - day + 1);
  return `${tmp.getFullYear()}-S${String(Math.ceil(
    ((tmp - new Date(tmp.getFullYear(), 0, 1)) / 86400000 + 1) / 7
  )).padStart(2, '00')}`;
};

// ─── Constantes seuils stock fort / stock faible ─────────────────────────────
const RATIO_FORT   = 2.0;
const RATIO_FAIBLE = 0.5;
const STOCK_FORT_ABS_FALLBACK   = 50;
const STOCK_FAIBLE_ABS_FALLBACK =  0;

// ─── Clé unique d'une ligne de recalage (pour les cases à cocher) ────────────
const cmdKey = (item, c, i) =>
  `${item}::${c['Numero de commande'] ?? ''}::${c['Date de livraison'] ?? ''}::${i}`;

// ─── Libellé / écart d'une ligne de recalage ─────────────────────────────────
const getRecalageInfo = (c) => {
  const statut = (c.statut || '').trim().toUpperCase();
  if (statut === 'ANNULATION') return { label: 'Annulation', bg: '#FEE2E2', color: '#B91C1C', diffJours: null };
  const origD   = parseLocalDate(c['Date de livraison']) || new Date(c['Date de livraison']);
  const reportD = parseLocalDate(c['Report proposé'])    || new Date(c['Report proposé']);
  if (!origD || !reportD || isNaN(origD) || isNaN(reportD)) {
    return { label: c.statut || '—', bg: '#E8F4FD', color: '#1565C0', diffJours: null };
  }
  const diffJours = Math.round((reportD - origD) / (1000 * 60 * 60 * 24));
  if (diffJours < 0) return { label: `Avancement (${Math.abs(diffJours)}j)`, bg: '#EAF3DE', color: '#3B6D11', diffJours };
  if (diffJours > 0) return { label: `Report (${diffJours}j)`,               bg: '#FFF3CD', color: '#856404', diffJours };
  return { label: 'Inchangé', bg: '#F3F4F6', color: '#6B7280', diffJours: 0 };
};

// ─── Agrégation ──────────────────────────────────────────────────────────────
const aggregateRows = (rows, granularity) => {
  if (granularity === 'jour') return rows.map(r => ({ ...r, _periodKey: r.date }));
  const groups = {};
  rows.forEach((r) => {
    const d = parseLocalDate(r.date);
    if (!d || isNaN(d)) return;
    const key = granularity === 'semaine' ? getWeekKey(d) : getMonthKey(d);
    if (!groups[key]) groups[key] = { lastRow: null, sumDepart: 0, sumLivraison: 0, sumPrevision: 0, sumCommandeFerme: 0 };
    const g = groups[key];
    if (!g.lastRow || parseLocalDate(r.date) >= parseLocalDate(g.lastRow.date)) g.lastRow = r;
    const lf = parseFloat(r['livraison fournisseur']); if (!isNaN(lf)) g.sumLivraison += lf;
    const dc = parseFloat(r['depart client']);
    if (!isNaN(dc)) {
      g.sumDepart += dc;
      if (r['type de donnee'] === 'prévision') g.sumPrevision += dc;
      if (r['type de donnee'] === 'commande ferme') g.sumCommandeFerme += dc;
    }
  });
  return Object.entries(groups).filter(([, g]) => g.lastRow).map(([key, g]) => ({
    ...g.lastRow,
    'depart client': g.sumDepart,
    'livraison fournisseur': g.sumLivraison,
    _sumPrevision: g.sumPrevision,
    _sumCommandeFerme: g.sumCommandeFerme,
    _periodKey: key,
  })).sort((a, b) => (parseLocalDate(a.date) || 0) - (parseLocalDate(b.date) || 0));
};

const formatLabel = (dateStr, granularity) => {
  const d = parseLocalDate(dateStr);
  if (!d || isNaN(d)) return dateStr || '';
  if (granularity === 'jour') return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  if (granularity === 'semaine') return `S${String(Math.ceil(
    ((new Date(d.getFullYear(), d.getMonth(), d.getDate()) - new Date(d.getFullYear(), 0, 1)) / 86400000 + 1) / 7
  )).padStart(2, '0')} ${d.getFullYear()}`;
  return d.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
};

// ─── Statuts & badges ─────────────────────────────────────────────────────────
const STATUT_CFG = {
  'ok':            { label: 'Ok',           bg: '#EAF3DE', color: '#3B6D11', dot: '#4CAF50' },
  'rupture':       { label: 'Rupture',      bg: '#FCEBEB', color: '#A32D2D', dot: '#E53935' },
  'rupture sèche': { label: 'Rupture sèche',bg: '#F7C1C1', color: '#791F1F', dot: '#B71C1C' },
  'stock faible':  { label: 'Stock faible', bg: '#FAEEDA', color: '#854F0B', dot: '#FF9800' },
  'stock fort':    { label: 'Stock fort',   bg: '#E8F4FD', color: '#1565C0', dot: '#1976D2' },
  'hausse':        { label: '↑ Hausse',     bg: '#EAF3DE', color: '#3B6D11', dot: '#4CAF50' },
  'baisse':        { label: '↓ Baisse',     bg: '#FCEBEB', color: '#A32D2D', dot: '#E53935' },
  'stable':        { label: '→ Stable',     bg: '#F3F4F6', color: '#6B7280', dot: '#9E9E9E' },
};

const StatusBadge = ({ statut, small }) => {
  const cfg = STATUT_CFG[statut] ?? { label: statut, bg: '#F3F4F6', color: '#6B7280', dot: '#9E9E9E' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: small ? '2px 8px' : '4px 11px',
      borderRadius: 20, fontSize: small ? 12 : 13, fontWeight: 500,
      background: cfg.bg, color: cfg.color, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
};

const CmdFrnBadge = ({ cmds }) => {
  if (!cmds || cmds.length === 0) return <span style={{ color: '#D1D5DB', fontSize: 12 }}>—</span>;
  const hasReport = cmds.some(c => c.statut === 'REPORT');
  const hasInf    = cmds.some(c => c.statut === 'inf +2 semaines');
  const bg    = hasInf ? '#FFF3CD' : hasReport ? '#E8F4FD' : '#EAF3DE';
  const color = hasInf ? '#856404' : hasReport ? '#1565C0' : '#3B6D11';
  const totalQty = cmds.reduce((s, c) => s + (parseFloat(c['Quantité commandée']) || 0), 0);
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: bg, color }}>
        {cmds.length} cmde{cmds.length > 1 ? 's' : ''} · {fmtQty(totalQty)} u.
      </span>
    </span>
  );
};

// ─── Calcul graphe ────────────────────────────────────────────────────────────
const buildChartData = (rows, granularity = 'mois') => {
  const agg = aggregateRows(rows, granularity);
  return {
    labels: agg.map((r) => formatLabel(r.date, granularity)),
    datasets: [
      {
        label: 'Stock projeté', type: 'line',
        data: agg.map((r) => Math.round(parseFloat(r['stock projete']) || 0)),
        borderColor: '#0D2140', backgroundColor: 'rgba(13,33,64,0.15)',
        borderWidth: 2.5, fill: false, tension: 0.3, datalabels: { display: false },
      },
      {
        label: 'Stock de sécurité', type: 'line',
        data: new Array(agg.length).fill(Math.round(parseFloat(agg[0]?.['stock mini'] || 0))),
        borderColor: '#E53935', backgroundColor: 'rgba(229,57,53,0.08)',
        borderWidth: 1.5, fill: false, borderDash: [6, 4], datalabels: { display: false },
      },
      {
        label: 'Prévision', type: 'bar',
        data: agg.map((r) => granularity === 'jour'
          ? (r['type de donnee'] === 'prévision' ? Math.round(parseFloat(r['depart client']) || 0) : 0)
          : Math.round(r._sumPrevision || 0)),
        backgroundColor: 'rgba(25,118,210,0.45)', borderColor: '#1976D2',
        borderWidth: 1, stack: 'out', datalabels: { display: ctx => ctx.dataset.data[ctx.dataIndex] !== 0, color: '#1565C0', anchor: 'end', align: 'top', font: { size: 10 } },
      },
      {
        label: 'Commande ferme', type: 'bar',
        data: agg.map((r) => granularity === 'jour'
          ? (r['type de donnee'] === 'commande ferme' ? Math.round(parseFloat(r['depart client']) || 0) : 0)
          : Math.round(r._sumCommandeFerme || 0)),
        backgroundColor: 'rgba(229,57,53,0.45)', borderColor: '#E53935',
        borderWidth: 1, stack: 'out', datalabels: { display: ctx => ctx.dataset.data[ctx.dataIndex] !== 0, color: '#B71C1C', anchor: 'end', align: 'top', font: { size: 10 } },
      },
      {
        label: 'Livraison fournisseur', type: 'bar',
        data: agg.map((r) => Math.round(parseFloat(r['livraison fournisseur']) || 0)),
        backgroundColor: 'rgba(255,152,0,0.5)', borderColor: '#FF9800',
        borderWidth: 1, stack: 'in', datalabels: { display: ctx => ctx.dataset.data[ctx.dataIndex] !== 0, color: '#E65100', anchor: 'end', align: 'top', font: { size: 10 } },
      },
    ],
  };
};

const chartOptions = {
  responsive: true, maintainAspectRatio: false,
  scales: {
    x: { stacked: false, ticks: { maxRotation: 45, minRotation: 0, font: { size: 11 } }, grid: { color: '#F3F4F6' } },
    y: { stacked: false, ticks: { font: { size: 11 } }, grid: { color: '#F3F4F6' } },
  },
  plugins: {
    legend: { display: true, labels: { font: { size: 12 }, boxWidth: 12 } },
    datalabels: { color: '#374151', font: { weight: 'bold', size: 10 } },
  },
};

// ─── Simulation ───────────────────────────────────────────────────────────────
const buildSimulatedRows = (rows, cmds) => {
  let simRows = rows.map(r => ({ ...r }));

  cmds.forEach(cmd => {
    const origDate   = cmd['Date de livraison'];
    const reportDate = cmd['Report proposé'];
    const statut     = (cmd.statut || '').trim().toUpperCase();
    const qty        = parseFloat(cmd['Quantité commandée']) || 0;

    if (statut === 'ANNULATION') {
      simRows = simRows.map(r => {
        const rDate = parseLocalDate(r.date);
        const oDate = parseLocalDate(origDate) || new Date(origDate);
        if (!rDate || !oDate || isNaN(rDate) || isNaN(oDate)) return r;
        if (rDate.toDateString() === oDate.toDateString() && r['type de donnee'] === 'commande frn') {
          return { ...r, 'livraison fournisseur': Math.max(0, (parseFloat(r['livraison fournisseur']) || 0) - qty) };
        }
        return r;
      });
    } else if (reportDate && reportDate !== 'ANNULATION') {
      const oDate = parseLocalDate(origDate) || new Date(origDate);
      const nDate = parseLocalDate(reportDate) || new Date(reportDate);
      if (!oDate || !nDate || isNaN(oDate) || isNaN(nDate)) return;

      simRows = simRows.map(r => {
        const rDate = parseLocalDate(r.date);
        if (!rDate || isNaN(rDate)) return r;
        if (rDate.toDateString() === oDate.toDateString() && r['type de donnee'] === 'commande frn') {
          return { ...r, 'livraison fournisseur': Math.max(0, (parseFloat(r['livraison fournisseur']) || 0) - qty) };
        }
        return r;
      });

      const existingIdx = simRows.findIndex(r => {
        const rDate = parseLocalDate(r.date);
        return rDate && !isNaN(rDate) && rDate.toDateString() === nDate.toDateString();
      });
      if (existingIdx >= 0) {
        simRows[existingIdx] = {
          ...simRows[existingIdx],
          'livraison fournisseur': (parseFloat(simRows[existingIdx]['livraison fournisseur']) || 0) + qty,
        };
      } else {
        const refRow = simRows[0] || {};
        const dd = nDate.getDate(), mm = nDate.getMonth() + 1, yyyy = nDate.getFullYear();
        simRows.push({
          ...refRow,
          date: `${String(dd).padStart(2,'0')}/${String(mm).padStart(2,'0')}/${yyyy}`,
          'type de donnee': 'commande frn',
          'livraison fournisseur': qty,
          'depart client': 0,
        });
      }
    }
  });

  simRows.sort((a, b) => (parseLocalDate(a.date)||0) - (parseLocalDate(b.date)||0));
  let stockCourant = parseFloat(simRows[0]?.stock) || 0;
  simRows = simRows.map((r) => {
    const lf = parseFloat(r['livraison fournisseur']) || 0;
    const dc = parseFloat(r['depart client']) || 0;
    stockCourant = stockCourant + lf - dc;
    return { ...r, 'stock projete': stockCourant };
  });

  return simRows;
};

// ─── Calcul stats par item ────────────────────────────────────────────────────
const _now = new Date();
const getMonthPeriod = (offset) => {
  const d = new Date(_now.getFullYear(), _now.getMonth() + offset, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const computeItemStats = (rows) => {
  const now  = new Date();
  const in30 = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  const sorted = [...rows].sort((a, b) => (parseLocalDate(a.date) || 0) - (parseLocalDate(b.date) || 0));
  const stock_actuel = parseFloat(sorted[0]?.stock) || 0;
  const stock_mini   = parseFloat(sorted[0]?.['stock mini']) || 0;
  const prix         = parseFloat(sorted[0]?.['Prix unitaire']) || 0;

  const conso30j = sorted.reduce((sum, r) => {
    const d = parseLocalDate(r.date);
    if (!d || isNaN(d)) return sum;
    if (d >= now && d <= in30) {
      const dc = parseFloat(r['depart client']);
      if (!isNaN(dc) && dc > 0) return sum + dc;
    }
    return sum;
  }, 0);

  const moisMap = {};
  sorted.forEach((r) => {
    const d = parseLocalDate(r.date);
    if (!d || isNaN(d)) return;
    const key = getMonthKey(d);
    if (!moisMap[key]) moisMap[key] = { analyses: [], stockProjete: null, stockMin: null, departClient: 0 };
    const a = (r['Analyse'] || '').trim().toLowerCase();
    if (a) moisMap[key].analyses.push(a);
    const sp = parseFloat(r['stock projete']);
    if (!isNaN(sp)) {
      moisMap[key].stockProjete = sp;
      if (moisMap[key].stockMin === null || sp < moisMap[key].stockMin) {
        moisMap[key].stockMin = sp;
      }
    }
    const dc = parseFloat(r['depart client']);
    if (!isNaN(dc)) moisMap[key].departClient += dc;
  });

  const getStatut = (stock, analyses) => {
    if (stock < 0) return 'rupture';
    if (stock === 0 && analyses.some(a => a === 'rupture sèche')) return 'rupture sèche';
    if (conso30j > 0) {
      if (stock > RATIO_FORT   * conso30j) return 'stock fort';
      if (stock < RATIO_FAIBLE * conso30j) return 'stock faible';
    } else {
      if (stock > STOCK_FORT_ABS_FALLBACK)   return 'stock fort';
      if (stock <= STOCK_FAIBLE_ABS_FALLBACK && stock > 0) return 'stock faible';
    }
    return 'ok';
  };

  const statutsMois = {};
  const ruptureInfraMensuelle = {};
  let dernierStatutConnu = null;
  for (let i = 0; i <= 11; i++) {
    const key = getMonthPeriod(i);
    const m = moisMap[key];
    if (!m || m.stockProjete == null) { statutsMois[i] = dernierStatutConnu; continue; }
    const s = getStatut(m.stockProjete, m.analyses);
    dernierStatutConnu = s;
    statutsMois[i] = s;
    if (m.stockMin !== null && m.stockMin < 0) {
      ruptureInfraMensuelle[i] = { stockMin: m.stockMin, stockFin: m.stockProjete };
    }
  }

  const stockM   = moisMap[getMonthPeriod(0)]?.stockProjete ?? null;
  const stockM6  = moisMap[getMonthPeriod(6)]?.stockProjete ?? null;
  // Dernier stock projeté = stock de la dernière ligne datée (fin de l'horizon des données),
  // et non plus le stock de fin de mois M+11 (qui peut être négatif alors que le dernier est positif)
  const derniereLigne = [...sorted].reverse().find(r => {
    const d = parseLocalDate(r.date);
    return d && !isNaN(d) && !isNaN(parseFloat(r['stock projete']));
  });
  const stockM12 = derniereLigne ? parseFloat(derniereLigne['stock projete']) : null;
  const dateDernierStock = derniereLigne ? parseLocalDate(derniereLigne.date) : null;

  const valeurStock = stock_actuel * prix;
  const valeurM12   = stockM12 != null ? stockM12 * prix : null;
  let pctEvol = null, tendance = 'stable';
  if (valeurStock !== 0 && valeurM12 != null) {
    pctEvol = Math.round(((valeurM12 - valeurStock) / Math.abs(valeurStock)) * 100);
    if (pctEvol > 30) tendance = 'hausse';
    else if (pctEvol < -30) tendance = 'baisse';
  }

  const allStatuts = Object.values(statutsMois).filter(Boolean);
  let risque = 'ok';
  if (allStatuts.includes('rupture'))        risque = 'rupture';
  else if (allStatuts.includes('rupture sèche')) risque = 'rupture sèche';
  else if (allStatuts.includes('stock faible'))  risque = 'stock faible';
  else if (allStatuts.filter(s => s === 'stock fort').length >= 6) risque = 'stock fort';

  return { stock_actuel, stock_mini, conso30j, prix, stockM, stockM6, stockM12, dateDernierStock, pctEvol, tendance, risque, statutsMois, valeurStock, valeurM12, ruptureInfraMensuelle };
};

// ─── Helpers Excel (ExcelJS) ──────────────────────────────────────────────────
const XL_NAVY    = '#0D2140';
const XL_FMT_QTY = '#,##0;[Red]-#,##0';
const XL_FMT_EUR = '#,##0 €;[Red]-#,##0 €';
const XL_FMT_EUR2 = '#,##0.00 €';
const XL_FMT_DATE = 'dd/mm/yyyy';

const xlArgb = (hex) => 'FF' + hex.replace('#', '').toUpperCase();
const xlFill = (hex) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb: xlArgb(hex) } });
const xlBorder = {
  top:    { style: 'thin', color: { argb: 'FFE5E7EB' } },
  left:   { style: 'thin', color: { argb: 'FFE5E7EB' } },
  bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
  right:  { style: 'thin', color: { argb: 'FFE5E7EB' } },
};

// Date "jour" sans décalage de fuseau (ExcelJS écrit en UTC)
const toExcelDate = (val) => {
  if (!val) return null;
  const d = parseLocalDate(val) || new Date(val);
  if (!d || isNaN(d)) return null;
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
};

const xlStyleHeader = (row) => {
  row.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
    cell.fill = xlFill(XL_NAVY);
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = xlBorder;
  });
  row.height = 24;
};

const xlStyleBody = (row, idx) => {
  row.eachCell({ includeEmpty: true }, (cell) => {
    cell.border = xlBorder;
    if (!cell.fill || cell.fill.type !== 'pattern') {
      if (idx % 2 === 1) cell.fill = xlFill('#F8FAFC');
    }
    cell.alignment = { vertical: 'middle', ...(cell.alignment || {}) };
  });
};

const xlTitle = (ws, text, subtitle, lastCol) => {
  ws.mergeCells(1, 1, 1, lastCol);
  const t = ws.getCell(1, 1);
  t.value = text;
  t.font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
  t.fill = xlFill(XL_NAVY);
  t.alignment = { vertical: 'middle', indent: 1 };
  ws.getRow(1).height = 28;
  if (subtitle) {
    ws.mergeCells(2, 1, 2, lastCol);
    const s = ws.getCell(2, 1);
    s.value = subtitle;
    s.font = { italic: true, size: 10, color: { argb: 'FF6B7280' } };
    s.alignment = { indent: 1 };
  }
};

const xlStatutCell = (cell, statut) => {
  const cfg = STATUT_CFG[statut];
  if (!cfg) { cell.value = statut || '—'; return; }
  cell.value = cfg.label;
  cell.fill = xlFill(cfg.bg);
  cell.font = { bold: true, color: { argb: xlArgb(cfg.color) } };
  cell.alignment = { horizontal: 'center', vertical: 'middle' };
};

/* ═════════════════════════════════════════════════════════════════════════════
   ALERTES STOCK : rupture / surstock / dormant (onglet « Alertes stock »)
   ROUGE  RUPTURE  : le stock projeté passe sous 0 dans les 12 mois
   ORANGE SURSTOCK : couverture > SEUIL_COUVERTURE_MOIS (2 mois), ou réceptions frn sans aucun besoin client
   GRIS   DORMANT  : du stock, mais aucun mouvement prévu sur 12 mois
   ═════════════════════════════════════════════════════════════════════════════ */

// ★ Paramètres des alertes (à ajuster si besoin)
const SEUIL_COUVERTURE_MOIS = 2;   // ★ au-delà de 2 mois de couverture = surstock
const MOIS_TAUX_COUV        = [3, 6, 9];   // ★ taux de couverture affichés : fin des mois M+3, M+6 et M+9
const HORIZON_MOIS          = 12;  // on ne regarde pas plus loin
const MOIS_MONTANT_SURSTOCK = 6;   // ★ montant du surstock calculé sur le stock net fin M+6 (ex. fin avril si on est en octobre)

// ─── Couleurs ─────────────────────────────────────────────────────────────────
const C = {
  navy: '#0D2140', fg: '#1D2430', muted: '#5F6B7A', line: '#E2E5EA', bg: '#F5F6F8', accent: '#2F5D8A',
};
const ALERTES = {
  RUPTURE:  { label: 'Rupture',  titre: 'Risque de rupture',  color: '#C2362B', bg: '#FBE9E7', action: 'Relancer ou avancer une commande fournisseur' },
  SURSTOCK: { label: 'Surstock', titre: 'Risque de surstock', color: '#B8650A', bg: '#FDF0DE', action: 'Reporter ou annuler des commandes' },
  DORMANT:  { label: 'Dormant',  titre: 'Stock dormant',      color: '#5C6672', bg: '#ECEEF1', action: 'Écouler, déprécier ou renvoyer' },
};
const ORDRE = { RUPTURE: 1, SURSTOCK: 2, DORMANT: 3 };
const SAIN  = { label: 'OK', color: '#15803D', bg: '#E8F5EC' };   // carte « Stock sain » et articles sans alerte
const CONSO_SEMAINES = 4;   // conso de référence = sorties client des 4 prochaines semaines

// ─── Formatters ───────────────────────────────────────────────────────────────
const fmtEur = (n) => (n
  ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n)
  : '—');
const fmtMillions = (n) => (n >= 1e6
  ? `${(n / 1e6).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} M€`
  : fmtEur(n));
const alQty = (n) => Number(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 1 });
const alJour = (d) => d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };

// Date jj/mm/aaaa ou ISO → Date à minuit
const parseDate = (v) => {
  if (!v) return null;
  let d;
  if (v instanceof Date) d = v;
  else {
    const s = String(v).trim();
    const p = s.split('/');
    d = p.length === 3 ? new Date(Number(p[2]), Number(p[1]) - 1, Number(p[0])) : new Date(s);
  }
  return isNaN(d) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

// Ajoute n jours ouvrés (lundi → vendredi) à une date
const ajouterJoursOuvres = (date, n) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  let reste = Math.max(0, Math.round(n));
  while (reste > 0) {
    d.setDate(d.getDate() + 1);
    const j = d.getDay();
    if (j !== 0 && j !== 6) reste--;
  }
  return d;
};

// ─── Recalage : ce qu'il y a à faire, une ligne par commande ────────────────
// "Cde 59482 · Avancer de 9 j (04/11 → 26/10) · 20 u."
const ACTIONS_MAX = 5;   // nb max d'actions affichées par ligne (le reste dans la fenêtre)
const ACTIONS_RECALAGE = {
  commander: { verbe: 'Commander', color: '#A32D2D' },
  avancer:  { verbe: 'Avancer',  color: '#3B6D11' },
  reporter: { verbe: 'Reporter', color: '#856404' },
  annuler:  { verbe: 'Annuler',  color: '#B91C1C' },
  cadencement: { verbe: 'Revoir cadencement', color: '#1E40AF' },
};
const actionsRecalage = (cmds = []) => {
  const jj = (d) => (d ? `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}` : '?');
  return [...cmds]
    .sort((a, b) => (parseDate(a['Date de livraison']) || 0) - (parseDate(b['Date de livraison']) || 0))
    .map(c => {
      const num_cde = c['Numero de commande'] ?? '';
      const qte = alQty(num(c['Quantité commandée']));
      const dLiv = parseDate(c['Date de livraison']);
      if ((c.statut || '').trim().toUpperCase() === 'ANNULATION') {
        return { type: 'annuler', texte: `Cde ${num_cde} · Annuler · ${qte} u.` };
      }
      const info = getRecalageInfo(c);
      if (info.diffJours == null || info.diffJours === 0) return null;
      const type = info.diffJours < 0 ? 'avancer' : 'reporter';
      const dNew = parseDate(c['Report proposé']);
      return {
        type,
        texte: `Cde ${num_cde} · ${ACTIONS_RECALAGE[type].verbe} de ${Math.abs(info.diffJours)} j (${jj(dLiv)} → ${jj(dNew)}) · ${qte} u.`,
      };
    })
    .filter(Boolean);
};

// ─── Calcul de l'alerte d'un article ──────────────────────────────────────────
// Retourne la liste des alertes de l'article ([] si aucune).
const calculerAlerte = (item, rows, cmds, aujourdhui, horizon) => {
  const lignes = rows
    .map(r => ({ ...r, _d: parseDate(r.date) }))
    .filter(r => r._d && r._d <= horizon)
    .sort((a, b) => a._d - b._d);
  if (!lignes.length) return [];

  const premiere    = lignes[0];
  const stockActuel = num(premiere['stock projete']) - num(premiere['livraison fournisseur']) + num(premiere['depart client']);
  // Conso de référence = sorties client des 4 prochaines semaines (≈ 1 mois)
  const finConso    = new Date(aujourdhui.getFullYear(), aujourdhui.getMonth(), aujourdhui.getDate() + 7 * CONSO_SEMAINES);
  const conso4sem   = lignes
    .filter(r => r._d >= aujourdhui && r._d < finConso)
    .reduce((s, r) => s + num(r['depart client']), 0);
  // Sorties sur tout l'horizon : servent seulement à savoir s'il y a un besoin client
  const sorties12   = lignes.reduce((s, r) => s + num(r['depart client']), 0);
  // Pas de sortie sur les 4 semaines mais des sorties plus tard : moyenne 12 mois en secours
  const consoSecours = conso4sem === 0 && sorties12 > 0;
  const consoMois   = consoSecours ? sorties12 / HORIZON_MOIS : conso4sem;
  const receptions  = lignes.reduce((s, r) => s + num(r['livraison fournisseur']), 0);
  const stockFin    = num(lignes[lignes.length - 1]['stock projete']);
  const prix        = Math.max(0, ...rows.map(r => num(r['Prix unitaire'])));
  const negatifs    = lignes.filter(r => num(r['stock projete']) < 0);
  const description = (rows.find(r => (r.description || '').trim())?.description || '').trim();

  // valeur       = montant de l'alerte (rupture : manque · surstock : surplus fin M+6 · dormant : stock)
  // valeurStock  = valeur du stock de référence du montant (surstock : stock net fin M+6)
  // valeurEnMain = valeur du stock actuel (stock en main × prix), colonne « Valeur stock »
  // Un article peut avoir PLUSIEURS alertes : ex. rupture d'abord, puis surstock
  // en fin de période → il apparaît dans « Risque de rupture » ET « Risque de surstock ».
  const commun = {
    item, description,
    stockActuel: Math.round(stockActuel), stockFin: Math.round(stockFin), consoMois, prix,
    valeurEnMain: Math.max(0, stockActuel) * prix,
    aMouvements: rows.some(r => (r['type de donnee'] || '').trim()),
  };
  const resultats = [];
  const fmtDateLongue = (d) => d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });

  // ── 1. RUPTURE ────────────────────────────────────────────────────────────
  let dateRupture = null;
  if (negatifs.length) {
    dateRupture = negatifs[0]._d;
    const manque = Math.round(-Math.min(...negatifs.map(r => num(r['stock projete']))));
    const enRetard = dateRupture < aujourdhui;
    const sansCouverture = lignes.some(r => (r.Analyse || '').trim().toLowerCase() === 'rupture sèche');
    // Délai d'appro (jours ouvrés, colonne "delais appro system") :
    // rupture APRÈS aujourd'hui + délai → on a le temps de commander = délai ok, sinon délai ko
    // Délai vide ou à 0 = délai manquant
    let delaiOk;
    const delais = rows.map(r => parseFloat(r['delais appro system'])).filter(v => !isNaN(v) && v > 0);
    if (delais.length) {
      const dateLimite = ajouterJoursOuvres(aujourdhui, Math.max(...delais));
      delaiOk = dateRupture > dateLimite ? 'ok' : 'ko';
    } else {
      delaiOk = 'manquant';
    }
    resultats.push({
      ...commun,
      alerte: 'RUPTURE',
      // "Rupture le 11/09/2026" (date sur 4 chiffres)
      detail: `Rupture le ${fmtDateLongue(dateRupture)}`,
      delaiOk, enRetard, sansCouverture, dateRupture,
      valeur: manque * prix, valeurStock: 0,
      // Rupture sans aucune commande frn pour couvrir : il faut commander
      actions: [
        ...(sansCouverture ? [{ type: 'commander', texte: `Placer commande fournisseur · ${alQty(manque)} u.` }] : []),
        ...actionsRecalage(cmds),
      ],
    });
  }

  // ── 2. SURSTOCK (testé même si l'article est en rupture) ──────────────────
  // Le surstock se juge sur le stock de FIN de période : un article peut être
  // en rupture au début puis en surstock à la fin.
  const apresRupture = dateRupture ? `Surstock après la rupture du ${fmtDateLongue(dateRupture)}` : null;

  // Stock net fin M+N = stock projeté fin M+N − besoin restant
  //   stock projeté fin M+N = dernière valeur connue à cette date (si les données s'arrêtent avant : dernière valeur disponible)
  //   besoin restant = pour chaque mois M+1 … M+N : conso − sorties déjà dans le système ce mois-là (jamais < 0)
  //   (le stock projeté a déjà déduit ces sorties : on ne les compte pas deux fois)
  //   ex. décembre : conso 46, sorties système 20 → on déduit 26 et non 46
  //   Sans conso (consoMois = 0) : besoin restant = 0 → stock projeté fin M+N
  const stockNetA = (nbMois) => {
    // Fin du mois M+N (jour 0 du mois suivant = dernier jour du mois)
    const finPeriode = new Date(aujourdhui.getFullYear(), aujourdhui.getMonth() + nbMois + 1, 0);
    const lignesAvant = lignes.filter(r => r._d <= finPeriode);
    const stockFinPeriode = lignesAvant.length ? num(lignesAvant[lignesAvant.length - 1]['stock projete']) : stockActuel;
    let besoinRestant = 0;
    for (let m = 1; m <= nbMois; m++) {
      const debutMois = new Date(aujourdhui.getFullYear(), aujourdhui.getMonth() + m, 1);
      const finMois   = new Date(aujourdhui.getFullYear(), aujourdhui.getMonth() + m + 1, 0);
      const sortiesSysteme = lignes
        .filter(r => r._d >= debutMois && r._d <= finMois)
        .reduce((t, r) => t + num(r['depart client']), 0);
      besoinRestant += Math.max(0, consoMois - sortiesSysteme);
    }
    return stockFinPeriode - besoinRestant;
  };
  // Nom du mois M+N : "janvier 2027"
  const nomMois = (n) => new Date(aujourdhui.getFullYear(), aujourdhui.getMonth() + n, 1)
    .toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  // Stock net à la date du montant (jamais < 0 pour la valorisation)
  const stockMontant = Math.max(0, stockNetA(MOIS_MONTANT_SURSTOCK));

  if (sorties12 === 0 && receptions > 0 && stockFin > 0) {
    // Aucun besoin client : tout le stock fin M+6 est en trop
    const valeurStock = stockMontant * prix;
    resultats.push({
      ...commun,
      alerte: 'SURSTOCK',
      detail: [apresRupture, 'Réceptions frn sans besoin client'].filter(Boolean).join('\n'),
      delaiOk: null, enRetard: false, sansCouverture: false, dateRupture,
      valeur: valeurStock, valeurStock,
      actions: actionsRecalage(cmds),
    });
  } else if (consoMois > 0 && stockFin / consoMois > SEUIL_COUVERTURE_MOIS) {
    // ★ Montant = surplus fin M+6 = stock net fin M+6 − SEUIL_COUVERTURE_MOIS × conso (jamais < 0)
    //   ex. stock net fin avril 797 u., conso 166 → 797 − 2 × 166 = 465 u. en trop × prix
    const surplus = Math.max(0, stockMontant - SEUIL_COUVERTURE_MOIS * consoMois);
    // Taux de couverture stock en main = stock actuel ÷ conso
    const couvEnMain = stockActuel / consoMois;
    // Taux de couverture stock à N mois = stock net fin M+N ÷ conso
    const tauxA = (nbMois) => stockNetA(nbMois) / consoMois;
    const fmtMois = (v) => `${v.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} mois`;
    // ★ Taux de couverture à 9 mois < 0 : le stock manquera plus tard, il ne faut pas annuler
    //   → les annulations sont remplacées par "Revoir cadencement"
    //   sinon (taux ≥ 0) : on garde les annulations proposées
    const taux9 = tauxA(9);
    let actionsSurstock = actionsRecalage(cmds);
    if (taux9 < 0) {
      actionsSurstock = [
        { type: 'cadencement', texte: 'Revoir cadencement' },
        ...actionsSurstock.filter(a => a.type !== 'annuler'),
      ];
    }
    resultats.push({
      ...commun,
      alerte: 'SURSTOCK',
      detail: [apresRupture,
        `Taux de couverture stock en main = ${fmtMois(couvEnMain)}`,
        ...MOIS_TAUX_COUV.map(n => `Taux de couverture stock fin ${nomMois(n)} = ${fmtMois(tauxA(n))}`)]
        .filter(Boolean).join('\n'),
      delaiOk: null, enRetard: false, sansCouverture: false, dateRupture,
      valeur: surplus * prix, valeurStock: stockMontant * prix,
      actions: actionsSurstock,
    });
  }

  // ── 3. DORMANT (seulement si aucune autre alerte) ─────────────────────────
  if (!resultats.length && sorties12 === 0 && receptions === 0 && stockActuel > 0) {
    const valeurStock = stockActuel * prix;
    resultats.push({
      ...commun,
      alerte: 'DORMANT',
      detail: `Aucun mouvement prévu sur ${HORIZON_MOIS} mois`,
      delaiOk: null, enRetard: false, sansCouverture: false, dateRupture: null,
      valeur: valeurStock, valeurStock,
      actions: actionsRecalage(cmds),
    });
  }

  return resultats;   // [] = article OK
};

// ─── Onglet « Alertes stock » ─────────────────────────────────────────────────
// Clic sur une ligne = ouverture de la fenêtre article (onOuvrir)
const VueAlertes = ({ rawData, recalageData, onOuvrir }) => {
  const [filtre, setFiltre]             = useState(null);   // null = toutes les alertes
  const [recherche, setRecherche]       = useState('');
  const [exportEnCours, setExportEnCours] = useState(false);

  // ─── Calcul des alertes ────────────────────────────────────────────────────
  // Lignes et commandes regroupées par article (servent aussi à la fenêtre)
  const { rowsParItem, cmdsParItem } = useMemo(() => {
    const cmds = {}, rows = {};
    recalageData.forEach(c => {
      const k = (c.Item || '').trim();
      if (k) (cmds[k] = cmds[k] || []).push(c);
    });
    rawData.forEach(r => {
      const k = (r.Item || '').trim();
      if (k) (rows[k] = rows[k] || []).push(r);
    });
    return { rowsParItem: rows, cmdsParItem: cmds };
  }, [rawData, recalageData]);

  const alertes = useMemo(() => {
    const now = new Date();
    const aujourdhui = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const horizon = new Date(now.getFullYear(), now.getMonth() + HORIZON_MOIS, now.getDate());

    return Object.entries(rowsParItem)
      // flatMap : un article peut donner plusieurs lignes (rupture + surstock)
      .flatMap(([item, rows]) => calculerAlerte(item, rows, cmdsParItem[item], aujourdhui, horizon))
      // Ruptures : la plus urgente d'abord · le reste : la plus grosse valeur d'abord
      .sort((a, b) => (ORDRE[a.alerte] - ORDRE[b.alerte])
        || (a.alerte === 'RUPTURE' ? a.dateRupture - b.dateRupture : 0)
        || (b.valeur - a.valeur));
  }, [rowsParItem, cmdsParItem]);

  // ─── Compteurs ─────────────────────────────────────────────────────────────
  const resume = useMemo(() => {
    const par = (k) => alertes.filter(a => a.alerte === k);
    const rupt = par('RUPTURE'), surs = par('SURSTOCK'), dorm = par('DORMANT');
    const somme = (liste, champ) => liste.reduce((s, a) => s + (a[champ] || 0), 0);
    return {
      RUPTURE:  { nb: rupt.length },
      SURSTOCK: { nb: surs.length, valeur: somme(surs, 'valeur'), legende: 'en trop' },
      DORMANT:  { nb: dorm.length, valeur: somme(dorm, 'valeur'), legende: 'immobilisés' },
    };
  }, [alertes]);

  // ─── Stock sain (carte verte) ──────────────────────────────────────────────
  // Base : valeur du stock ACTUEL de tous les articles (stock en main × prix), alertes ou non.
  // Stock sain = valeur totale − part en trop aujourd'hui − stock dormant
  //   part en trop (surstock) = stock actuel au-delà de SEUIL_COUVERTURE_MOIS mois de conso
  //                             (sans besoin client : tout le stock actuel)
  //   dormant                 = tout le stock actuel
  // Les ruptures restent dans le stock sain (le stock en main sert).
  // Détail par article (clic sur la carte « Stock sain ») : valeur stock, part non saine, part saine.
  const santeStock = useMemo(() => {
    const now = new Date();
    const horizon = new Date(now.getFullYear(), now.getMonth() + HORIZON_MOIS, now.getDate());
    const alertesParItem = {};
    alertes.forEach(a => { (alertesParItem[a.item] = alertesParItem[a.item] || []).push(a); });

    const articles = [];
    Object.entries(rowsParItem).forEach(([item, rows]) => {
      const lignes = rows
        .map(r => ({ ...r, _d: parseDate(r.date) }))
        .filter(r => r._d && r._d <= horizon)
        .sort((a, b) => a._d - b._d);
      if (!lignes.length) return;
      const p = lignes[0];
      const stockActuel = num(p['stock projete']) - num(p['livraison fournisseur']) + num(p['depart client']);
      const prix = Math.max(0, ...rows.map(r => num(r['Prix unitaire'])));
      const valeurStock = Math.max(0, stockActuel) * prix;
      if (valeurStock <= 0) return;

      const als  = alertesParItem[item] || [];
      const dorm = als.find(a => a.alerte === 'DORMANT');
      const surs = als.find(a => a.alerte === 'SURSTOCK');
      const rupt = als.find(a => a.alerte === 'RUPTURE');
      let nonSain = 0;
      let motif = 'Aucune alerte';
      if (dorm) {
        nonSain = valeurStock;
        motif = 'Dormant : tout le stock est exclu';
      } else if (surs && surs.consoMois > 0) {
        const qteSaine = SEUIL_COUVERTURE_MOIS * surs.consoMois;
        nonSain = Math.max(0, stockActuel - qteSaine) * prix;
        motif = nonSain > 0
          ? `Surstock : sain jusqu'à ${SEUIL_COUVERTURE_MOIS} mois de conso (${alQty(Math.round(qteSaine))} u.)`
          : `Surstock à venir : stock actuel ≤ ${SEUIL_COUVERTURE_MOIS} mois de conso`;
      } else if (surs) {
        nonSain = valeurStock;
        motif = 'Surstock sans besoin client : tout le stock est exclu';
      } else if (rupt) {
        motif = 'Rupture : le stock en main sert';
      }
      const description = (rows.find(r => (r.description || '').trim())?.description || '').trim();
      articles.push({
        item, description, statut: dorm ? 'DORMANT' : surs ? 'SURSTOCK' : rupt ? 'RUPTURE' : null,
        stockActuel: Math.round(stockActuel), prix, valeurStock, nonSain, sain: valeurStock - nonSain, motif,
      });
    });
    articles.sort((a, b) => b.sain - a.sain);

    const total = articles.reduce((s, a) => s + a.valeurStock, 0);
    const sain  = articles.reduce((s, a) => s + a.sain, 0);
    return {
      total, sain, pct: total ? Math.round((sain / total) * 100) : 0,
      nbArticles: articles.length,
      articles: articles.filter(a => a.sain > 0),   // détail : seulement les articles avec du stock sain
    };
  }, [rowsParItem, alertes]);

  // ─── Filtre + recherche ────────────────────────────────────────────────────
  const lignes = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    return alertes.filter(a => (!filtre || a.alerte === filtre)
      && (!q || `${a.item} ${a.description}`.toLowerCase().includes(q)));
  }, [alertes, filtre, recherche]);

  // Filtre « SAIN » : la table affiche le détail du stock sain au lieu des alertes
  const vueSain = filtre === 'SAIN';
  const lignesSain = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    return santeStock.articles.filter(a => !q || `${a.item} ${a.description}`.toLowerCase().includes(q));
  }, [santeStock, recherche]);
  const nbLignes    = vueSain ? lignesSain.length : lignes.length;
  const titreFiltre = vueSain ? 'Stock sain' : filtre ? ALERTES[filtre].titre : 'Toutes les alertes';

  const choisirFiltre = (k) => setFiltre(f => (f === k ? null : k));

  // ★ Mois du montant surstock (M+6) pour l'en-tête : "avril", puis "mai" le mois suivant…
  const moisMontant = (() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth() + MOIS_MONTANT_SURSTOCK, 1).toLocaleDateString('fr-FR', { month: 'long' });
  })();

  // ─── Export Excel des lignes affichées (filtre + recherche) ────────────────
  const libDelai = { ok: 'Délais ok', ko: 'Délais ko', manquant: 'Délais manquant' };
  const exporterExcel = async () => {
    if (!nbLignes) return;
    setExportEnCours(true);
    try {
      const mod = await import('exceljs');
      const ExcelJS = mod.default ?? mod;
      const wb = new ExcelJS.Workbook();
      wb.creator = 'MYSSA';
      wb.created = new Date();
      const sousTitre = `Export du ${new Date().toLocaleString('fr-FR')} · ${nbLignes} ligne(s)${recherche.trim() ? ` · recherche « ${recherche.trim()} »` : ''}`;
      const telecharger = async (nomFichier) => {
        const buf  = await wb.xlsx.writeBuffer();
        const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url  = URL.createObjectURL(blob);
        const lien = document.createElement('a');
        lien.href = url;
        lien.download = `${nomFichier}_${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(lien);
        lien.click();
        lien.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      };

      // ── Export du détail « Stock sain » ──
      if (vueSain) {
        const ws = wb.addWorksheet('Stock sain', { views: [{ state: 'frozen', ySplit: 4, showGridLines: false }] });
        const cols = [
          ['Article', 26], ['Description', 34], ['Alerte', 12], ['Stock', 10], ['Prix unitaire €', 14],
          ['Valeur stock €', 16], ['Part non saine €', 16], ['Stock sain €', 16], ['Commentaire', 60],
        ];
        ws.columns = cols.map(([, w]) => ({ width: w }));
        xlTitle(ws, 'Stock sain — détail par article', sousTitre, cols.length);
        const h = ws.getRow(4); h.values = cols.map(([t]) => t); xlStyleHeader(h);
        lignesSain.forEach((a, i) => {
          const row = ws.getRow(5 + i);
          row.values = [
            a.item, a.description, a.statut ? ALERTES[a.statut].label : 'OK', a.stockActuel, a.prix,
            Math.round(a.valeurStock), Math.round(a.nonSain) || null, Math.round(a.sain), a.motif,
          ];
          row.getCell(4).numFmt = '#,##0;[Red]-#,##0';
          row.getCell(5).numFmt = XL_FMT_EUR2;
          [6, 7, 8].forEach(ci => { row.getCell(ci).numFmt = XL_FMT_EUR; });
          xlStyleBody(row, i);
        });
        const last = 4 + lignesSain.length;
        ws.autoFilter = { from: { row: 4, column: 1 }, to: { row: last, column: cols.length } };
        const tot = ws.getRow(last + 1);
        tot.getCell(5).value = 'Total';
        tot.getCell(6).value = { formula: `SUM(F5:F${last})` };
        tot.getCell(7).value = { formula: `SUM(G5:G${last})` };
        tot.getCell(8).value = { formula: `SUM(H5:H${last})` };
        [5, 6, 7, 8].forEach(ci => { tot.getCell(ci).font = { bold: true }; });
        [6, 7, 8].forEach(ci => { tot.getCell(ci).numFmt = XL_FMT_EUR; });
        await telecharger('MYSSA_stock_sain');
        return;
      }

      const nomFiltre = titreFiltre;
      const ws = wb.addWorksheet('Alertes stock', { views: [{ state: 'frozen', ySplit: 4, showGridLines: false }] });
      const cols = [
        ['Alerte', 12], ['Article', 26], ['Description', 34], ['Stock', 10], ['Valeur stock €', 14], ['Conso 4 sem / CMM', 18],
        ['Stock fin de période', 14], ['Analyse', 62], [`Montant fin ${moisMontant} €`, 18], [`Stock de référence du montant €`, 18], ['Action', 60],
      ];
      ws.columns = cols.map(([, w]) => ({ width: w }));
      xlTitle(ws, `Alertes stock — ${nomFiltre}`, sousTitre, cols.length);
      const h = ws.getRow(4); h.values = cols.map(([t]) => t); xlStyleHeader(h);

      lignes.forEach((a, i) => {
        const cfg = ALERTES[a.alerte];
        const analyse = a.detail + (a.delaiOk ? ` / ${libDelai[a.delaiOk]}` : '');
        const row = ws.getRow(5 + i);
        row.values = [
          cfg.label, a.item, a.description, a.stockActuel, Math.round(a.valeurEnMain) || null, a.consoMois || null, a.stockFin,
          analyse, Math.round(a.valeur) || null, Math.round(a.valeurStock) || null,
          a.actions.map(x => x.texte).join('\n'),
        ];
        [4, 6, 7].forEach(ci => { row.getCell(ci).numFmt = '#,##0.#;[Red]-#,##0.#'; row.getCell(ci).alignment = { horizontal: 'center' }; });
        [5, 9, 10].forEach(ci => { row.getCell(ci).numFmt = XL_FMT_EUR; });
        row.getCell(8).alignment = { wrapText: true, vertical: 'middle' };
        row.getCell(11).alignment = { wrapText: true, vertical: 'middle' };
        xlStyleBody(row, i);
        const c1 = row.getCell(1);
        c1.fill = xlFill(cfg.bg);
        c1.font = { bold: true, color: { argb: xlArgb(cfg.color) } };
        c1.alignment = { horizontal: 'center', vertical: 'middle' };
      });
      const last = 4 + lignes.length;
      ws.autoFilter = { from: { row: 4, column: 1 }, to: { row: last, column: cols.length } };
      const tot = ws.getRow(last + 1);
      // ★ Valeur stock (col. E) : un article en rupture + surstock a 2 lignes → total à lire avec prudence
      tot.getCell(4).value = 'Total';
      tot.getCell(5).value = { formula: `SUM(E5:E${last})` };
      tot.getCell(9).value = { formula: `SUM(I5:I${last})` };
      tot.getCell(10).value = { formula: `SUM(J5:J${last})` };
      [4, 5, 9, 10].forEach(ci => { tot.getCell(ci).font = { bold: true }; });
      [5, 9, 10].forEach(ci => { tot.getCell(ci).numFmt = XL_FMT_EUR; });

      await telecharger(`MYSSA_alertes_stock_${filtre ? filtre.toLowerCase() : 'toutes'}`);
    } catch (e) {
      console.error(e);
      alert(`Erreur lors de l'export Excel : ${e.message}`);
    } finally {
      setExportEnCours(false);
    }
  };

  // ─── Styles ────────────────────────────────────────────────────────────────
  const th = { fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.muted, fontWeight: 600, textAlign: 'center', padding: '10px 12px', borderBottom: `1px solid ${C.line}`, whiteSpace: 'nowrap', background: '#FFFFFF', position: 'sticky', top: 0 };
  const thN = th;
  const td = { padding: '9px 12px', borderBottom: `1px solid ${C.line}`, verticalAlign: 'middle', textAlign: 'center', fontSize: 13 };
  const tdN = { ...td, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
  const encadre = { background: '#FFFFFF', border: `1px solid ${C.line}`, borderRadius: 8 };

  return (
    <div style={{
      padding: 0, width: '100%', boxSizing: 'border-box',
      fontFamily: 'IBM Plex Sans, -apple-system, sans-serif', color: C.fg,
      display: 'flex', flexDirection: 'column', gap: 18,
    }}>

      {/* ── En-tête ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0, color: C.navy }}>Alertes stock</h1>
        <span style={{ color: C.muted, fontSize: 13 }}>
          Calculé le {alJour(new Date())} · horizon {HORIZON_MOIS} mois · {new Set(alertes.map(a => a.item)).size} articles en alerte
        </span>
      </div>

      {/* ── Cartes : 3 alertes + stock sain (cliquables) + stock total ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
        {Object.entries(ALERTES).map(([k, cfg]) => {
          const actif = filtre === k;
          const r = resume[k];
          return (
            <button key={k} type="button" onClick={() => choisirFiltre(k)} aria-pressed={actif} style={{
              all: 'unset', boxSizing: 'border-box', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', gap: 4, padding: '16px 18px', borderRadius: 8,
              background: actif ? cfg.bg : '#FFFFFF',
              border: `1px solid ${actif ? cfg.color : C.line}`, borderTop: `4px solid ${cfg.color}`,
            }}>
              <span style={{ fontWeight: 600, color: cfg.color, fontSize: 13, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{cfg.titre}</span>
              <span style={{ fontSize: 32, fontWeight: 600, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
                {k !== 'RUPTURE' && r.valeur
                  ? <>{fmtMillions(r.valeur)} <span style={{ fontSize: 14, fontWeight: 400, color: C.muted }}>{r.legende}</span></>
                  : <>{r.nb} <span style={{ fontSize: 14, fontWeight: 400, color: C.muted }}>article{r.nb > 1 ? 's' : ''}</span></>}
              </span>
              <span style={{ fontSize: 13, marginTop: 6 }}>→ {cfg.action}</span>
            </button>
          );
        })}

        {/* ── 4e carte : stock sain (cliquable → détail par article) ── */}
        <button type="button" onClick={() => choisirFiltre('SAIN')} aria-pressed={vueSain}
          title={`Valeur du stock actuel hors stock dormant et hors part au-delà de ${SEUIL_COUVERTURE_MOIS} mois de conso — clic pour le détail`}
          style={{
            all: 'unset', boxSizing: 'border-box', cursor: 'pointer',
            display: 'flex', flexDirection: 'column', gap: 4, padding: '16px 18px', borderRadius: 8,
            background: vueSain ? SAIN.bg : '#FFFFFF',
            border: `1px solid ${vueSain ? SAIN.color : C.line}`, borderTop: `4px solid ${SAIN.color}`,
          }}>
          <span style={{ fontWeight: 600, color: SAIN.color, fontSize: 13, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Stock sain</span>
          <span style={{ fontSize: 32, fontWeight: 600, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
            {fmtMillions(santeStock.sain)} <span style={{ fontSize: 14, fontWeight: 400, color: C.muted }}>sains</span>
          </span>
          <span style={{ fontSize: 13, marginTop: 6 }}>→ {santeStock.pct} % du stock actuel · voir le détail</span>
        </button>

        {/* ── 5e carte : stock total (information, non cliquable) ── */}
        <div title="Valeur du stock actuel de tous les articles (stock en main × prix unitaire)"
          style={{
            boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 4, padding: '16px 18px', borderRadius: 8,
            background: '#FFFFFF', border: `1px solid ${C.line}`, borderTop: `4px solid ${C.navy}`,
          }}>
          <span style={{ fontWeight: 600, color: C.navy, fontSize: 13, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Stock total</span>
          <span style={{ fontSize: 32, fontWeight: 600, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
            {fmtMillions(santeStock.total)} <span style={{ fontSize: 14, fontWeight: 400, color: C.muted }}>en stock</span>
          </span>
          <span style={{ fontSize: 13, marginTop: 6 }}>→ {santeStock.nbArticles} articles en stock aujourd'hui</span>
        </div>
      </div>

      {/* ── Recherche ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
        <input type="search" value={recherche}
          onChange={e => setRecherche(e.target.value)}
          placeholder="Chercher un article ou une description" aria-label="Chercher un article"
          style={{ font: 'inherit', fontSize: 14, padding: '8px 12px', border: `1px solid ${C.line}`, borderRadius: 6, background: '#FFFFFF', width: 320, maxWidth: '100%', boxSizing: 'border-box', margin: 0 }} />
        <button type="button" onClick={exporterExcel} disabled={exportEnCours || !nbLignes}
          title="Exporte les lignes affichées (filtre et recherche en cours)"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, width: 'auto', margin: 0, height: 36,
            padding: '0 16px', borderRadius: 8, border: 'none', whiteSpace: 'nowrap',
            background: exportEnCours || !nbLignes ? '#9CA3AF' : '#1D6F42', color: '#FFFFFF',
            fontSize: 13, fontWeight: 600, cursor: exportEnCours ? 'wait' : 'pointer',
          }}>
          {exportEnCours ? 'Export…' : `Excel · ${titreFiltre} (${nbLignes})`}
        </button>
      </div>

      {/* ── Table détail « Stock sain » (carte verte cliquée) ── */}
      {vueSain && (
        <div style={{ ...encadre, overflow: 'auto', maxHeight: '65vh' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
            <thead>
              <tr>
                <th style={th}>Article</th>
                <th style={th}>Alerte</th>
                <th style={thN}>Stock</th>
                <th style={thN}>Prix unitaire</th>
                <th style={thN} title="Stock actuel × prix unitaire">Valeur stock</th>
                <th style={thN} title="Stock dormant, ou stock actuel au-delà de 2 mois de conso">Part non saine</th>
                <th style={{ ...thN, color: SAIN.color }}>Stock sain</th>
                <th style={th}>Commentaire</th>
              </tr>
            </thead>
            <tbody>
              {lignesSain.map(a => {
                const cfg = a.statut ? ALERTES[a.statut] : SAIN;
                return (
                  <tr key={a.item} onClick={() => onOuvrir(a.item)} title="Voir le graphique et les commandes"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#F8FAFC'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                    <td style={td}>
                      <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 12.5, fontWeight: 600, color: C.navy, whiteSpace: 'nowrap' }}>{a.item}</div>
                      <div style={{ color: C.muted, fontSize: 12.5 }}>{a.description}</div>
                    </td>
                    <td style={td}>
                      <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', padding: '2px 8px', borderRadius: 999, color: cfg.color, background: cfg.bg, whiteSpace: 'nowrap' }}>
                        {cfg.label}
                      </span>
                    </td>
                    <td style={tdN}>{alQty(a.stockActuel)}</td>
                    <td style={tdN}>{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(a.prix)}</td>
                    <td style={tdN}>{fmtEur(a.valeurStock)}</td>
                    <td style={{ ...tdN, color: a.nonSain > 0 ? ALERTES.SURSTOCK.color : C.muted }}>{fmtEur(a.nonSain)}</td>
                    <td style={{ ...tdN, fontWeight: 600, color: SAIN.color }}>{fmtEur(a.sain)}</td>
                    <td style={{ ...td, fontSize: 12.5, color: C.muted }}>{a.motif}</td>
                  </tr>
                );
              })}
              {!lignesSain.length && (
                <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: C.muted, padding: '2rem' }}>Aucun article.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Table des alertes ── */}
      {!vueSain && (
      <div style={{ ...encadre, overflow: 'auto', maxHeight: '65vh' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
          <thead>
            <tr>
              <th style={th}>Alerte</th>
              <th style={th}>Article</th>
              <th style={thN}>Stock</th>
              <th style={thN} title="Stock actuel × prix unitaire">Valeur stock</th>
              <th style={thN} title="Sorties client des 4 prochaines semaines">Conso 4 sem / CMM</th>
              <th style={thN} title="Dernière valeur du stock projeté à la fin de l'horizon">Stock fin de période</th>
              <th style={{ ...th, whiteSpace: 'nowrap' }}>Analyse</th>
              <th style={thN} title={`Rupture : manque × prix · Surstock : stock net fin ${moisMontant} au-delà de ${SEUIL_COUVERTURE_MOIS} mois de conso × prix · Dormant : stock × prix`}>Montant fin {moisMontant}</th>
              <th style={th}>Action</th>
            </tr>
          </thead>
          <tbody>
            {lignes.map(a => {
              const cfg = ALERTES[a.alerte];
              return (
                <tr key={`${a.item}-${a.alerte}`} onClick={() => onOuvrir(a.item)} title="Voir le graphique et les commandes"
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#F8FAFC'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                  <td style={td}>
                    <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', padding: '2px 8px', borderRadius: 999, color: cfg.color, background: cfg.bg, whiteSpace: 'nowrap' }}>
                      {cfg.label}
                    </span>
                  </td>
                  <td style={td}>
                    <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 12.5, fontWeight: 600, color: C.navy, whiteSpace: 'nowrap' }}>{a.item}</div>
                    <div style={{ color: C.muted, fontSize: 12.5 }}>{a.description}</div>
                  </td>
                  <td style={tdN}>{alQty(a.stockActuel)}</td>
                  <td style={tdN}>{fmtEur(a.valeurEnMain)}</td>
                  <td style={tdN}>{a.consoMois ? alQty(a.consoMois) : '—'}</td>
                  <td style={{ ...tdN, color: a.stockFin < 0 ? ALERTES.RUPTURE.color : C.fg }}>{alQty(a.stockFin)}</td>
                  {/* ★ 'pre' : une phrase = une ligne, jamais coupée (la colonne s'élargit) */}
                  <td style={{ ...td, whiteSpace: 'pre' }}>
                    {a.detail}
                    {a.delaiOk != null && (
                      <> / <span style={{ fontWeight: 600, color: a.delaiOk === 'ok' ? '#15803D' : ALERTES.RUPTURE.color }}>
                        {a.delaiOk === 'ok' ? 'Délais ok' : a.delaiOk === 'ko' ? 'Délais ko' : 'Délais manquant'}
                      </span></>
                    )}
                  </td>
                  <td style={tdN}>
                    <div style={{ fontWeight: a.alerte === 'RUPTURE' ? 400 : 600 }}>{fmtEur(a.valeur)}</div>
                    {a.alerte === 'SURSTOCK' && a.valeurStock > a.valeur && (
                      <div style={{ fontSize: 11.5, color: C.muted }}>sur {fmtEur(a.valeurStock)} de stock</div>
                    )}
                  </td>
                  <td style={{ ...td, fontSize: 12.5 }}>
                    {a.actions.length
                      ? <>
                        {a.actions.slice(0, ACTIONS_MAX).map((x, i) => (
                          <div key={i} style={{ color: ACTIONS_RECALAGE[x.type].color, fontWeight: 500, whiteSpace: 'nowrap', lineHeight: 1.6 }}>{x.texte}</div>
                        ))}
                        {a.actions.length > ACTIONS_MAX && (
                          <div style={{ color: C.muted }}>+ {a.actions.length - ACTIONS_MAX} autre{a.actions.length - ACTIONS_MAX > 1 ? 's' : ''} (clic pour le détail)</div>
                        )}
                      </>
                      : <span style={{ color: '#D1D5DB' }}>—</span>}
                  </td>
                </tr>
              );
            })}
            {!lignes.length && (
              <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: C.muted, padding: '2rem' }}>Aucune alerte pour ce filtre.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      )}

    </div>
  );
};


// ─── Composant principal ──────────────────────────────────────────────────────
const Stockprojete = () => {
  const [rawData, setRawData]           = useState([]);
  const [recalageData, setRecalageData] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [granularity, setGranularity]   = useState('mois');
  const [excludedCmds, setExcludedCmds] = useState({});   // { [cmdKey]: true } = recalage décoché
  const [exporting, setExporting]       = useState(false);

  useEffect(() => { document.title = 'MYSSA – Stock projeté'; }, []);


  // ─── Cases à cocher recalage (simulation uniquement) ───────────────────────
  const isCmdActive = (item, c, i) => !excludedCmds[cmdKey(item, c, i)];
  const toggleCmd = (item, c, i) => {
    const k = cmdKey(item, c, i);
    setExcludedCmds(prev => {
      const next = { ...prev };
      if (next[k]) delete next[k]; else next[k] = true;
      return next;
    });
  };
  const toggleAllCmds = (item, cmds, checked) => {
    setExcludedCmds(prev => {
      const next = { ...prev };
      cmds.forEach((c, i) => {
        const k = cmdKey(item, c, i);
        if (checked) delete next[k]; else next[k] = true;
      });
      return next;
    });
  };

  useEffect(() => {
    Promise.all([
      fetch(`${API}/donnee_histogramme`).then(r => { if (!r.ok) throw new Error(`Erreur ${r.status}`); return r.json(); }),
      fetch(`${API}/recalage_cmde_frn`).then(r => { if (!r.ok) throw new Error(`Erreur ${r.status}`); return r.json(); }),
    ])
      .then(([histo, recalage]) => {
        setRawData(histo);
        setRecalageData(recalage);
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  const recalageByItem = useMemo(() => {
    const idx = {};
    recalageData.forEach(r => {
      const item = (r.Item || '').trim();
      if (!item) return;
      if (!idx[item]) idx[item] = [];
      idx[item].push(r);
    });
    // Tri chronologique des commandes par date de livraison (tableau modale, simulation, export)
    const toTime = (v) => {
      const d = parseLocalDate(v) || (v ? new Date(v) : null);
      return d && !isNaN(d) ? d.getTime() : Infinity;
    };
    Object.values(idx).forEach(list =>
      list.sort((a, b) => toTime(a['Date de livraison']) - toTime(b['Date de livraison']))
    );
    return idx;
  }, [recalageData]);

  const itemSummary = useMemo(() => {
    if (!rawData.length) return [];
    const map = {};
    rawData.forEach(row => {
      const item = (row.Item || '').trim();
      if (!item) return;
      if (!map[item]) map[item] = { item, description: (row.description || '').trim(), rows: [] };
      map[item].rows.push(row);
    });
    return Object.values(map).map(({ item, description, rows }) => {
      const stats = computeItemStats(rows);
      const cmds  = recalageByItem[item] || [];
      const hasReport   = cmds.some(c => c.statut === 'REPORT');
      const hasDecalage = cmds.some(c => c.statut === 'inf +2 semaines');

      const gainAnnulation = cmds
        .filter(c => (c.statut || '').toUpperCase() === 'ANNULATION')
        .reduce((sum, c) => sum + (parseFloat(c['Quantité commandée']) || 0) * stats.prix, 0);

      const gainCashMoyen = (() => {
        if (!cmds.length) return 0;
        const sortedRows = [...rows].sort((a, b) => (parseLocalDate(a.date)||0) - (parseLocalDate(b.date)||0));
        const simuRows   = buildSimulatedRows(sortedRows, cmds);
        const baseAgg    = aggregateRows(sortedRows, 'mois');
        const simuAgg    = aggregateRows(simuRows, 'mois');
        const simuMap    = {};
        simuAgg.forEach(r => { simuMap[r._periodKey] = parseFloat(r['stock projete']) || 0; });
        const ecarts = baseAgg.map(r => {
          const sb = parseFloat(r['stock projete']) || 0;
          const ss = simuMap[r._periodKey] ?? sb;
          return (sb - ss) * stats.prix;
        });
        return ecarts.length ? ecarts.reduce((s, v) => s + v, 0) / ecarts.length : 0;
      })();

      const gainTotal = gainAnnulation + Math.max(0, gainCashMoyen);

      return { item, description, rows, cmds, hasReport, hasDecalage, gainAnnulation, gainCashMoyen, gainTotal, ...stats };
    });
  }, [rawData, recalageByItem]);

  const modalChartData = useMemo(() => {
    if (!selectedItem) return null;
    const it = itemSummary.find(i => i.item === selectedItem);
    if (!it) return null;
    const rows = [...it.rows].sort((a, b) => (parseLocalDate(a.date)||0) - (parseLocalDate(b.date)||0));
    return buildChartData(rows, granularity);
  }, [selectedItem, itemSummary, granularity]);

  // ─── Simulation modale : uniquement les recalages cochés ──────────────────
  const modalSimuChartData = useMemo(() => {
    if (!selectedItem) return null;
    const it = itemSummary.find(i => i.item === selectedItem);
    if (!it || !it.cmds.length) return null;

    const activeCmds = it.cmds.filter((c, i) => isCmdActive(it.item, c, i));
    if (!activeCmds.length) return null;   // rien de coché → pas de courbe simulée

    const rows = [...it.rows].sort((a, b) => (parseLocalDate(a.date)||0) - (parseLocalDate(b.date)||0));
    const simuRows = buildSimulatedRows(rows, activeCmds);
    const base = buildChartData(rows, granularity);

    const baseAggForChart = aggregateRows(rows, granularity);
    const simuAggForChart = aggregateRows(simuRows, granularity);
    const simuMapForChart = {};
    simuAggForChart.forEach(r => { simuMapForChart[r._periodKey] = parseFloat(r['stock projete']) || 0; });
    const simuDataAligned = baseAggForChart.map(r => {
      const stockBase = parseFloat(r['stock projete']) || 0;
      const stockSimu = simuMapForChart[r._periodKey] ?? stockBase;
      return Math.round(stockSimu);
    });

    const gainAnnulation = activeCmds
      .filter(c => (c.statut || '').toUpperCase() === 'ANNULATION')
      .reduce((sum, c) => sum + (parseFloat(c['Quantité commandée']) || 0) * it.prix, 0);

    const baseAggMois = aggregateRows(rows, 'mois');
    const simuAggMois = aggregateRows(simuRows, 'mois');
    const simuStockMap = {};
    simuAggMois.forEach(r => { simuStockMap[r._periodKey] = parseFloat(r['stock projete']) || 0; });
    const ecarts = baseAggMois.map(r => {
      const stockBase = parseFloat(r['stock projete']) || 0;
      const stockSimu = simuStockMap[r._periodKey] ?? stockBase;
      return (stockBase - stockSimu) * it.prix;
    }).filter(v => !isNaN(v));
    const gainCashMoyen = ecarts.length > 0
      ? ecarts.reduce((s, v) => s + v, 0) / ecarts.length
      : 0;

    return {
      chartData: {
        ...base,
        datasets: [
          ...base.datasets,
          {
            label: 'Stock projeté (après reports/annulations)',
            type: 'line',
            data: simuDataAligned,
            borderColor: '#1D9E75',
            backgroundColor: 'rgba(29,158,117,0.12)',
            borderWidth: 2.5,
            borderDash: [6, 3],
            fill: false,
            tension: 0.3,
            datalabels: { display: false },
          },
        ],
      },
      gainAnnulation,
      gainCashMoyen,
      activeCount: activeCmds.length,
    };
  }, [selectedItem, itemSummary, granularity, excludedCmds]);

  const activeChartData = modalSimuChartData?.chartData ?? modalChartData;

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column', gap: 12, color: '#6B7280', fontFamily: 'IBM Plex Sans, sans-serif' }}>
      <div style={{ width: 36, height: 36, border: '3px solid #E5E7EB', borderTopColor: '#0D2140', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      Chargement des stocks...
    </div>
  );
  if (error) return <div style={{ padding: '2rem', color: '#A32D2D' }}>Erreur : {error}</div>;

  const selectedItemData = selectedItem ? itemSummary.find(i => i.item === selectedItem) : null;
  const activeCmdCount = selectedItemData
    ? selectedItemData.cmds.filter((c, i) => isCmdActive(selectedItem, c, i)).length
    : 0;
  const allCmdsChecked  = selectedItemData && activeCmdCount === selectedItemData.cmds.length;
  const someCmdsChecked = activeCmdCount > 0 && !allCmdsChecked;

  // ─── Export Excel de la modale ─────────────────────────────────────────────
  const handleExportExcel = async () => {
    if (!selectedItemData || !activeChartData) return;
    setExporting(true);
    try {
      const mod = await import('exceljs');
      const ExcelJS = mod.default ?? mod;
      const wb = new ExcelJS.Workbook();
      wb.creator = 'MYSSA';
      wb.created = new Date();

      const it = selectedItemData;
      const gainAnn  = modalSimuChartData?.gainAnnulation ?? 0;
      const gainCash = modalSimuChartData?.gainCashMoyen ?? 0;
      const exportLabel = `Export du ${new Date().toLocaleString('fr-FR')} · granularité graphe : ${granularity}`;

      // ── Feuille 1 : Synthèse ────────────────────────────────────────────
      const ws1 = wb.addWorksheet('Synthèse', { views: [{ showGridLines: false }] });
      ws1.columns = [{ width: 40 }, { width: 22 }, { width: 30 }];
      xlTitle(ws1, `${it.item} — ${it.description}`, exportLabel, 3);

      let r = 4;
      const hdr1 = ws1.getRow(r); hdr1.values = ['Indicateur', 'Valeur']; xlStyleHeader(hdr1);
      const kv = [
        ['Statut global',                          null,  null, it.risque],
        ['Stock actuel',                           it.stock_actuel, XL_FMT_QTY],
        ['Prix unitaire',                          it.prix, XL_FMT_EUR2],
        ['Valeur stock actuel',                    it.valeurStock, XL_FMT_EUR],
        ['Consommation client 30 j',               it.conso30j, XL_FMT_QTY],
        ['Stock de sécurité',                      it.stock_mini, XL_FMT_QTY],
        ['Dernier stock projeté',                  it.stockM12 != null ? Math.round(it.stockM12) : null, XL_FMT_QTY],
        ['Date du dernier stock projeté',          it.dateDernierStock ? toExcelDate(it.dateDernierStock) : null, XL_FMT_DATE],
        ['Valeur dernier stock projeté',           it.valeurM12, XL_FMT_EUR],
        ['% évolution valeur (actuel → dernier)',     it.pctEvol != null ? it.pctEvol / 100 : null, '+0%;[Red]-0%;0%'],
        ['Encours libéré (annulations cochées)',   gainAnn, XL_FMT_EUR],
        ['Optim. trésorerie (moy. mensuelle)',     gainCash, XL_FMT_EUR],
        ['Recalages pris en compte',               `${activeCmdCount} / ${it.cmds.length}`],
      ];
      kv.forEach(([label, val, numFmt, statut], idx) => {
        r++;
        const row = ws1.getRow(r);
        row.getCell(1).value = label;
        row.getCell(1).font = { bold: true, color: { argb: 'FF374151' } };
        const c2 = row.getCell(2);
        if (statut) xlStatutCell(c2, statut);
        else {
          c2.value = val ?? '—';
          if (numFmt && val != null) c2.numFmt = numFmt;
          c2.alignment = { horizontal: 'right' };
        }
        xlStyleBody(row, idx);
      });

      r += 2;
      const hdr2 = ws1.getRow(r); hdr2.values = ['Mois', 'Statut', 'Rupture infra-mensuelle (stock mini)']; xlStyleHeader(hdr2);
      for (let i = 0; i <= 11; i++) {
        r++;
        const row = ws1.getRow(r);
        row.getCell(1).value = `${i === 0 ? 'M' : `M+${i}`} (${getMoisLabel(i)})`;
        const s = it.statutsMois[i];
        if (s) xlStatutCell(row.getCell(2), s); else row.getCell(2).value = '—';
        const ri = it.ruptureInfraMensuelle?.[i];
        const c3 = row.getCell(3);
        if (ri) {
          c3.value = Math.round(ri.stockMin);
          c3.numFmt = XL_FMT_QTY;
          c3.fill = xlFill('#FEF3C7');
          c3.font = { bold: true, color: { argb: 'FF92400E' } };
        } else c3.value = '—';
        c3.alignment = { horizontal: 'center' };
        xlStyleBody(row, i);
      }

      // ── Feuille 2 : Commandes fournisseur ───────────────────────────────
      if (it.cmds.length) {
        const ws2 = wb.addWorksheet('Commandes fournisseur', { views: [{ state: 'frozen', ySplit: 4, showGridLines: false }] });
        const cols2 = [
          { header: 'Pris en compte',   width: 14 },
          { header: 'Fournisseur',      width: 28 },
          { header: 'N° commande',      width: 15 },
          { header: 'Date livraison',   width: 15 },
          { header: 'Qté',              width: 10 },
          { header: 'Report proposé',   width: 15 },
          { header: 'Statut',           width: 18 },
          { header: 'Écart (jours)',    width: 13 },
          { header: 'Valeur commande',  width: 17 },
          { header: 'Approvisionneur',  width: 20 },
        ];
        ws2.columns = cols2.map(c => ({ width: c.width }));
        xlTitle(ws2, `Commandes fournisseur — ${it.item}`, `${activeCmdCount} recalage(s) pris en compte sur ${it.cmds.length}`, cols2.length);
        const h = ws2.getRow(4); h.values = cols2.map(c => c.header); xlStyleHeader(h);

        it.cmds.forEach((c, i) => {
          const active = isCmdActive(it.item, c, i);
          const info = getRecalageInfo(c);
          const isAnnul = (c.statut || '').trim().toUpperCase() === 'ANNULATION';
          const qty = parseFloat(c['Quantité commandée']) || 0;
          const row = ws2.getRow(5 + i);
          row.values = [
            active ? 'Oui' : 'Non',
            c.Fournisseur || '',
            c['Numero de commande'] ?? '',
            toExcelDate(c['Date de livraison']),
            qty,
            isAnnul ? '—' : toExcelDate(c['Report proposé']),
            info.label,
            info.diffJours,
            qty * it.prix,
            c.Approvisionneur || '',
          ];
          row.getCell(4).numFmt = XL_FMT_DATE;
          row.getCell(5).numFmt = XL_FMT_QTY;
          row.getCell(6).numFmt = XL_FMT_DATE;
          row.getCell(9).numFmt = XL_FMT_EUR;
          [1, 4, 6, 7, 8].forEach(ci => { row.getCell(ci).alignment = { horizontal: 'center' }; });

          const c1 = row.getCell(1);
          c1.fill = xlFill(active ? '#EAF3DE' : '#F3F4F6');
          c1.font = { bold: true, color: { argb: active ? 'FF3B6D11' : 'FF9CA3AF' } };
          const c7 = row.getCell(7);
          c7.fill = xlFill(info.bg);
          c7.font = { bold: true, color: { argb: xlArgb(info.color) } };

          xlStyleBody(row, i);
          if (!active) {
            [2, 3, 4, 5, 6, 8, 9, 10].forEach(ci => {
              row.getCell(ci).font = { color: { argb: 'FF9CA3AF' }, strike: true };
            });
          }
        });

        const lastRow = 4 + it.cmds.length;
        const tot = ws2.getRow(lastRow + 1);
        tot.getCell(4).value = 'Total';
        tot.getCell(5).value = { formula: `SUM(E5:E${lastRow})` };
        tot.getCell(9).value = { formula: `SUM(I5:I${lastRow})` };
        tot.getCell(5).numFmt = XL_FMT_QTY;
        tot.getCell(9).numFmt = XL_FMT_EUR;
        [4, 5, 9].forEach(ci => {
          const cell = tot.getCell(ci);
          cell.font = { bold: true };
          cell.border = { top: { style: 'medium', color: { argb: xlArgb(XL_NAVY) } } };
        });
        ws2.autoFilter = { from: { row: 4, column: 1 }, to: { row: lastRow, column: cols2.length } };
      }

      // ── Feuille 3 : Données du graphique ────────────────────────────────
      const ws3 = wb.addWorksheet('Données graphe', { views: [{ state: 'frozen', ySplit: 4, xSplit: 1, showGridLines: false }] });
      const ds = activeChartData.datasets;
      const simuIdx = ds.findIndex(d => d.label.startsWith('Stock projeté (après'));
      const hasSimu = simuIdx >= 0;
      const headers3 = ['Période', ...ds.map(d => d.label)];
      if (hasSimu) headers3.push('Écart stock (base − simulé)', 'Écart valeur €');
      ws3.columns = headers3.map((_, i) => ({ width: i === 0 ? 14 : 20 }));
      xlTitle(ws3, `Projection de stock — ${it.item}`, exportLabel, headers3.length);
      const h3 = ws3.getRow(4); h3.values = headers3; xlStyleHeader(h3);

      const stockCols = new Set(ds.map((d, i) => (d.label.startsWith('Stock projeté') ? i + 2 : null)).filter(Boolean));
      const secuIdx = ds.findIndex(d => d.label === 'Stock de sécurité');

      activeChartData.labels.forEach((lab, li) => {
        const values = ds.map(d => d.data[li] ?? null);
        const row = ws3.getRow(5 + li);
        const line = [lab, ...values];
        if (hasSimu) {
          const base = values[0] ?? 0;
          const simu = values[simuIdx] ?? 0;
          line.push(base - simu, (base - simu) * it.prix);
        }
        row.values = line;
        for (let ci = 2; ci <= line.length; ci++) {
          row.getCell(ci).numFmt = (hasSimu && ci === line.length) ? XL_FMT_EUR : XL_FMT_QTY;
        }
        row.getCell(1).font = { bold: true, color: { argb: xlArgb(XL_NAVY) } };
        row.getCell(1).alignment = { horizontal: 'center' };
        xlStyleBody(row, li);
        // Mise en évidence rupture / sous stock de sécurité
        const secu = secuIdx >= 0 ? values[secuIdx] ?? 0 : 0;
        stockCols.forEach(ci => {
          const v = row.getCell(ci).value;
          if (typeof v !== 'number') return;
          if (v < 0) {
            row.getCell(ci).fill = xlFill('#FCEBEB');
            row.getCell(ci).font = { bold: true, color: { argb: 'FFA32D2D' } };
          } else if (v < secu) {
            row.getCell(ci).fill = xlFill('#FAEEDA');
            row.getCell(ci).font = { bold: true, color: { argb: 'FF854F0B' } };
          }
        });
      });

      // ── Feuille 4 : Détail journalier brut ──────────────────────────────
      const ws4 = wb.addWorksheet('Détail journalier', { views: [{ state: 'frozen', ySplit: 4, showGridLines: false }] });
      const headers4 = ['Date', 'Type de donnée', 'Départ client', 'Livraison fournisseur', 'Stock projeté', 'Analyse'];
      ws4.columns = [{ width: 13 }, { width: 18 }, { width: 15 }, { width: 20 }, { width: 15 }, { width: 22 }];
      xlTitle(ws4, `Détail journalier — ${it.item}`, 'Données sources (hors simulation)', headers4.length);
      const h4 = ws4.getRow(4); h4.values = headers4; xlStyleHeader(h4);
      const rowsSorted = [...it.rows].sort((a, b) => (parseLocalDate(a.date)||0) - (parseLocalDate(b.date)||0));
      rowsSorted.forEach((src, i) => {
        const row = ws4.getRow(5 + i);
        const num = (v) => { const n = parseFloat(v); return isNaN(n) ? null : n; };
        row.values = [
          toExcelDate(src.date),
          src['type de donnee'] || '',
          num(src['depart client']),
          num(src['livraison fournisseur']),
          num(src['stock projete']),
          src['Analyse'] || '',
        ];
        row.getCell(1).numFmt = XL_FMT_DATE;
        [3, 4, 5].forEach(ci => { row.getCell(ci).numFmt = XL_FMT_QTY; });
        row.getCell(1).alignment = { horizontal: 'center' };
        xlStyleBody(row, i);
        const sp = row.getCell(5).value;
        if (typeof sp === 'number' && sp < 0) {
          row.getCell(5).fill = xlFill('#FCEBEB');
          row.getCell(5).font = { bold: true, color: { argb: 'FFA32D2D' } };
        }
      });
      ws4.autoFilter = { from: { row: 4, column: 1 }, to: { row: 4 + rowsSorted.length, column: headers4.length } };

      // ── Téléchargement ──────────────────────────────────────────────────
      const buf  = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      const safe = it.item.replace(/[^\w.-]+/g, '_');
      a.href = url;
      a.download = `MYSSA_stock_projete_${safe}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error(e);
      alert(`Erreur lors de l'export Excel : ${e.message}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{
      padding: '1rem 24px 1rem 0',            // ★ marge à droite : augmente la 2e valeur pour décaler plus vers la gauche
      width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden',
      fontFamily: "IBM Plex Sans, -apple-system, sans-serif", color: '#1A1A1A',
    }}>

      <VueAlertes rawData={rawData} recalageData={recalageData} onOuvrir={setSelectedItem} />

      {/* ── Modale graphique ── */}
      {selectedItem && modalChartData && selectedItemData && (
        <>
          <div onClick={() => setSelectedItem(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(13,33,64,0.5)', zIndex: 1000 }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: 'min(1020px, 96vw)', maxHeight: '94vh', overflowY: 'auto',
            background: '#FFFFFF', border: '0.5px solid #E5E7EB', borderRadius: 14,
            padding: '1.75rem', zIndex: 1001, boxShadow: '0 16px 48px rgba(13,33,64,0.22)',
          }}>
            {/* Header modale */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 17, fontWeight: 600, color: '#0D2140' }}>{selectedItem}</span>
                  <StatusBadge statut={selectedItemData.risque} />
                </div>
                <div style={{ fontSize: 13, color: '#6B7280' }}>{selectedItemData.description}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <button onClick={handleExportExcel} disabled={exporting} title="Exporter les données de la fenêtre en Excel" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 14px', borderRadius: 8, border: 'none',
                  background: exporting ? '#9CA3AF' : '#1D6F42', color: '#FFFFFF',
                  fontSize: 13, fontWeight: 600, cursor: exporting ? 'wait' : 'pointer',
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="9" y1="13" x2="15" y2="19" /><line x1="15" y1="13" x2="9" y2="19" />
                  </svg>
                  {exporting ? 'Export…' : 'Excel'}
                </button>
                <button onClick={() => setSelectedItem(null)} style={{
                  background: '#F3F4F6', border: 'none', cursor: 'pointer', color: '#6B7280',
                  fontSize: 16, width: 32, height: 32, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>✕</button>
              </div>
            </div>

            {/* Métriques rapides */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 20 }}>
              {[
                { label: 'Prix unitaire',      value: new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(selectedItemData.prix || 0), color: '#374151' },
                { label: 'Stock actuel',       value: fmtQty(selectedItemData.stock_actuel),                                                             color: '#0D2140' },
                { label: 'Valeur stock actuel',value: fmt(selectedItemData.valeurStock),                                                                  color: '#374151' },
                { label: 'Dernier stock projeté', value: selectedItemData.stockM12 != null ? fmtQty(Math.round(selectedItemData.stockM12)) : '—',           color: selectedItemData.stockM12 != null && selectedItemData.stockM12 < 0 ? '#A32D2D' : '#1565C0' },
                { label: 'Valeur dernier stock', value: selectedItemData.valeurM12 != null ? fmt(selectedItemData.valeurM12) : '—',                         color: selectedItemData.valeurM12 != null && selectedItemData.valeurM12 < 0 ? '#A32D2D' : '#374151' },
              ].map(m => (
                <div key={m.label} style={{ background: '#F8FAFC', borderRadius: 8, padding: '10px 14px', border: '0.5px solid #E5E7EB' }}>
                  <div style={{ fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{m.label}</div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: m.color, fontFamily: 'IBM Plex Mono, monospace' }}>{m.value}</div>
                </div>
              ))}
            </div>

            {/* Gains financiers (recalages cochés uniquement) */}
            {modalSimuChartData && (modalSimuChartData.gainAnnulation > 0 || modalSimuChartData.gainCashMoyen > 0) && (
              <div style={{ display: 'grid', gridTemplateColumns: modalSimuChartData.gainAnnulation > 0 && modalSimuChartData.gainCashMoyen > 0 ? '1fr 1fr' : '1fr', gap: 12, marginBottom: 20 }}>
                {modalSimuChartData.gainAnnulation > 0 && (
                  <div style={{ background: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)', border: '1.5px solid #86EFAC', borderRadius: 10, padding: '14px 18px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 18 }}>💰</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Encours fournisseur libéré</span>
                    </div>
                    <div style={{ fontSize: 26, fontWeight: 700, color: '#15803D', fontFamily: 'IBM Plex Mono, monospace', marginBottom: 4 }}>
                      +{fmt(modalSimuChartData.gainAnnulation)}
                    </div>
                    <div style={{ fontSize: 11, color: '#4ADE80', fontWeight: 500 }}>
                      Commandes annulées — montant non décaissé
                    </div>
                  </div>
                )}
                {modalSimuChartData.gainCashMoyen > 0 && (
                  <div style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)', border: '1.5px solid #93C5FD', borderRadius: 10, padding: '14px 18px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 18 }}>📉</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#1E40AF', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Optimisation trésorerie</span>
                    </div>
                    <div style={{ fontSize: 26, fontWeight: 700, color: '#1D4ED8', fontFamily: 'IBM Plex Mono, monospace', marginBottom: 4 }}>
                      +{fmt(modalSimuChartData.gainCashMoyen)}
                    </div>
                    <div style={{ fontSize: 11, color: '#60A5FA', fontWeight: 500 }}>
                      Économie moyenne mensuelle de trésorerie immobilisée
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Commandes fournisseur */}
            {selectedItemData.cmds.length > 0 && (
              <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#1565C0', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                  ⟳ Commandes fournisseur — recalages proposés
                </div>
                <div style={{ fontSize: 11, color: '#1E40AF', background: '#DBEAFE', borderRadius: 6, padding: '5px 10px', marginBottom: 10 }}>
                  {activeCmdCount > 0
                    ? <>La courbe verte en pointillés représente le stock projeté après application des recalages cochés (<b>{activeCmdCount}/{selectedItemData.cmds.length}</b>). Décoche une ligne pour l'exclure de la simulation.</>
                    : <>Aucun recalage coché — la courbe simulée est masquée.</>}
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12, textAlign: 'center' }}>
                    <thead>
                      <tr style={{ background: '#DBEAFE' }}>
                        <th style={{ padding: '6px 10px', width: 34, textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            title="Tout cocher / décocher"
                            checked={!!allCmdsChecked}
                            ref={el => { if (el) el.indeterminate = someCmdsChecked; }}
                            onChange={e => toggleAllCmds(selectedItem, selectedItemData.cmds, e.target.checked)}
                            style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#0D2140' }}
                          />
                        </th>
                        {['Fournisseur', 'N° commande', 'Date livraison', 'Qté', 'Report proposé', 'Statut', 'Approvisionneur'].map(h => (
                          <th key={h} style={{ padding: '6px 10px', textAlign: 'center', color: '#1E40AF', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedItemData.cmds.map((c, i) => {
                        const fmtD = (val) => {
                          if (!val) return '—';
                          const d = parseLocalDate(val) || new Date(val);
                          if (!d || isNaN(d)) return val;
                          return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                        };
                        const statutLabel = getRecalageInfo(c);
                        const active  = isCmdActive(selectedItem, c, i);
                        const isAnnul = c.statut?.toUpperCase() === 'ANNULATION';
                        const strike  = active ? {} : { textDecoration: 'line-through' };

                        return (
                          <tr key={i} style={{
                            borderBottom: '0.5px solid #BFDBFE',
                            background: i % 2 === 0 ? '#F0F7FF' : '#FFFFFF',
                            opacity: !active ? 0.45 : isAnnul ? 0.75 : 1,
                            transition: 'opacity 0.15s',
                          }}>
                            <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={active}
                                onChange={() => toggleCmd(selectedItem, c, i)}
                                title={active ? 'Recalage pris en compte — décocher pour l\'exclure' : 'Recalage exclu — cocher pour l\'inclure'}
                                style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#0D2140' }}
                              />
                            </td>
                            <td style={{ padding: '6px 10px', fontWeight: 500, color: '#1E40AF', ...strike }}>{c.Fournisseur}</td>
                            <td style={{ padding: '6px 10px', fontFamily: 'IBM Plex Mono, monospace', color: '#374151', ...strike }}>{c['Numero de commande']}</td>
                            <td style={{ padding: '6px 10px', color: '#374151', ...strike }}>{fmtD(c['Date de livraison'])}</td>
                            <td style={{ padding: '6px 10px', fontWeight: 600, textAlign: 'center', color: '#0D2140', ...strike }}>{fmtQty(c['Quantité commandée'])}</td>
                            <td style={{ padding: '6px 10px', color: c['Report proposé'] === 'ANNULATION' ? '#A32D2D' : '#854F0B', fontWeight: 500, ...strike }}>
                              {isAnnul ? <span style={{ color: '#B91C1C', fontWeight: 700 }}>—</span> : fmtD(c['Report proposé'])}
                            </td>
                            <td style={{ padding: '6px 10px' }}>
                              <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: statutLabel.bg, color: statutLabel.color, whiteSpace: 'nowrap', ...strike }}>
                                {statutLabel.label}
                              </span>
                            </td>
                            <td style={{ padding: '6px 10px', color: '#6B7280', fontSize: 11 }}>{c.Approvisionneur}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Sélecteur granularité */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              {[['jour','Jour'],['semaine','Semaine'],['mois','Mois']].map(([k, label]) => (
                <button key={k} onClick={() => setGranularity(k)} style={{
                  padding: '5px 14px', borderRadius: 16, cursor: 'pointer', fontSize: 12, fontWeight: granularity === k ? 700 : 400,
                  border: `1.5px solid ${granularity === k ? '#0D2140' : '#D1D5DB'}`,
                  background: granularity === k ? '#0D2140' : '#FFFFFF',
                  color: granularity === k ? '#FFFFFF' : '#374151', transition: 'all 0.15s',
                }}>{label}</button>
              ))}
            </div>

            {/* Graphe */}
            <div style={{ width: '100%', height: 380 }}>
              <Line data={activeChartData} options={chartOptions} />
            </div>

            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setSelectedItem(null)} style={{
                padding: '7px 22px', borderRadius: 6, cursor: 'pointer',
                background: '#F3F4F6', border: '0.5px solid #D1D5DB', fontSize: 14, color: '#374151',
              }}>Fermer</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Stockprojete;